import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getItemDB, decompressFromIDB } from '../components/commons';
import useProjectStore from './projectStore';

/**
 * Zustand store for core app data.
 *
 * Adding a new slice: add one entry to SLICE_DEFS, write one fetch function
 * in CoreStoreProvider, and export one useEnsureX hook from useCoreData.js.
 * Everything else (state, setters, ensure*, hydrate*, invalidate, resetAll) is generated.
 *
 * Cache TTL: default 30 min, override via localStorage.cacheTTLMin = 10
 * Invalidation: dispatch window event 'appfactor:data-invalidated'
 */

const _ttlMin = parseFloat(localStorage.cacheTTLMin);
export const CACHE_TTL_MS = (Number.isFinite(_ttlMin) && _ttlMin >= 0 ? _ttlMin : 30) * 60 * 1000;

/** Returns true if cachedAt is set and within the TTL window. */
export function isFresh(cachedAt, ttlMs = CACHE_TTL_MS) {
  return !!cachedAt && Date.now() - cachedAt < ttlMs;
}

const cap = key => key.charAt(0).toUpperCase() + key.slice(1);

/**
 * Central slice registry. One entry per data slice.
 *
 * key         — used for: xLoading, xCachedAt, refetchX, ensureX, _hydrating, _retries
 * dataField   — store field that holds the array (usually same as key)
 * idbKey      — IndexedDB key for persistence
 * idKey       — field used for fingerprinting (dedup check)
 * fetchRef    — which fetch group in CoreStoreProvider handles this slice
 * decompress  — whether IDB value needs decompressFromIDB before use
 * onLoad      — optional side-effect called with data after IDB hydration
 */
export const SLICE_DEFS = [
  { key: 'hosts',             dataField: 'hosts',             idbKey: 'hosts',                    idKey: 'machineId',     fetchRef: 'hosts' },
  { key: 'applications',      dataField: 'applications',      idbKey: 'applications',             idKey: 'id',            fetchRef: 'applications' },
  { key: 'vulnerabilities',   dataField: 'vulnerabilities',   idbKey: 'hostsWithVulnerabilities', idKey: 'machineId',     fetchRef: 'secondary', decompress: true },
  { key: 'installedPackages', dataField: 'installedPackagesAgg', idbKey: 'installedPackagesAgg', idKey: 'machineId',     fetchRef: 'secondary' },
  { key: 'assessmentsSummary',dataField: 'assessmentsSummary',idbKey: 'assessmentsSummary',       idKey: 'applicationId', fetchRef: 'assessments' },
  { key: 'enrichmentResults', dataField: 'enrichmentResults', idbKey: 'enrichmentResults',        idKey: 'id',            fetchRef: 'enrichmentResults' },
  { key: 'artifactRepositories', dataField: 'artifactRepositories', idbKey: 'artifactRepositories', idKey: 'id',          fetchRef: 'artifactRepositories' },
  { key: 'externalCmdbRecords',  dataField: 'externalCmdbRecords',  idbKey: 'externalCmdbRecords',  idKey: 'id',          fetchRef: 'externalCmdbRecords' },
  { key: 'appGraphData',      dataField: 'appGraphData',      idbKey: 'appGraphData',                                     fetchRef: 'appGraphData', isObject: true },
  { key: 'hostsGraph',        dataField: 'hostsGraph',        idbKey: 'hostsGraph',                                       fetchRef: 'hostsGraph',   isObject: true },
];

// ── Hydration guards — one entry per slice, reset on project change ──
export const _retries  = Object.fromEntries(SLICE_DEFS.map(d => [d.key, 0]));
const _hydrating = Object.fromEntries(SLICE_DEFS.map(d => [d.key, false]));
const MAX_RETRIES = 5;

function resetHydrating() {
  SLICE_DEFS.forEach(d => { _hydrating[d.key] = false; _retries[d.key] = 0; });
}

// ── IDB hydration helpers — generated per slice, hosts overridden below ──
const _hydrateFromIDB = {};
for (const def of SLICE_DEFS) {
  _hydrateFromIDB[def.key] = async () => {
    if (_hydrating[def.key]) return;
    _hydrating[def.key] = true;
    try {
      const currentProjectId = useProjectStore.getState().currentProjectId;
      if (!currentProjectId) return;
      let entry = await getItemDB(`${def.idbKey}--${currentProjectId}`);
      if (def.decompress && entry && typeof entry === 'string') entry = await decompressFromIDB(entry);
      const s = useCoreStore.getState();
      const empty = def.isObject ? !s[def.dataField] : !s[def.dataField].length;
      if (empty && entry?._data) {
        if (def.onLoad) def.onLoad(entry._data);
        s[`set${cap(def.dataField)}`](entry._data, entry._cachedAt);
      }
    } catch (_) { /* IDB unavailable — noop, network fetch follows */ }
    finally { _hydrating[def.key] = false; }
  };
}

// Hosts override: reads two IDB keys + sets systems companion via custom setHosts
_hydrateFromIDB.hosts = async () => {
  if (_hydrating.hosts) return;
  _hydrating.hosts = true;
  try {
    const currentProjectId = useProjectStore.getState().currentProjectId;
    if (!currentProjectId) return;
    const [hostsEntry, systemsEntry] = await Promise.all([
      getItemDB(`hosts--${currentProjectId}`),
      getItemDB(`systems--${currentProjectId}`),
    ]);
    const s = useCoreStore.getState();
    if (!s.hosts.length && hostsEntry?._data && systemsEntry?._data) {
      s.setHosts(hostsEntry._data, systemsEntry._data, hostsEntry._cachedAt);
    }
  } catch (_) { /* noop */ }
  finally { _hydrating.hosts = false; }
};

const useCoreStore = create(devtools((set, get) => {
  // ── Generated setters: set${cap(dataField)}(data, ts) ──
  // Hosts is excluded — it has a non-standard 3-arg signature (hosts, systems, ts).
  const setters = {};
  for (const def of SLICE_DEFS) {
    if (def.key === 'hosts') continue;
    setters[`set${cap(def.dataField)}`] = (data, ts) =>
      set({ [def.dataField]: data, [`${def.key}CachedAt`]: ts ?? Date.now() });
  }

  // ── Generated ensure* actions ──
  const ensurers = {};
  for (const def of SLICE_DEFS) {
    const K = cap(def.key);
    ensurers[`ensure${K}`] = () => {
      const s = get();
      const empty = def.isObject ? !s[def.dataField] : !s[def.dataField].length;
      if (empty) _hydrateFromIDB[def.key]();
      if (s[`${def.key}Loading`]) return;
      if (isFresh(s[`${def.key}CachedAt`])) return;
      const refetch = s[`refetch${K}`];
      if (!refetch) return;
      if (_retries[def.key] >= MAX_RETRIES) return;
      _retries[def.key]++;
      refetch();
    };
  }

  // ── Generated initial slice state ──
  const sliceState = {};
  for (const def of SLICE_DEFS) {
    sliceState[def.dataField]           = def.isObject ? null : [];
    sliceState[`${def.key}Loading`]     = false;
    sliceState[`${def.key}CachedAt`]    = null;
    sliceState[`refetch${cap(def.key)}`] = null;
  }

  return {
    // Non-slice fields
    systems: [],
    refetchSystems: null, // companion to hosts, not a full slice
    _invalidationSeq: 0,

    ...sliceState,

    // Custom setter for hosts — takes (hosts, systems, ts)
    setHosts: (hosts, systems, ts) =>
      set({ hosts, systems, hostsCachedAt: ts ?? Date.now() }),

    ...setters,

    setLoading: (key, val) => set({ [key]: val }),
    setRefetch: (refetches) => set(refetches),

    invalidate: (slices = SLICE_DEFS.map(d => d.key)) => {
      const patch = { _invalidationSeq: (get()._invalidationSeq ?? 0) + 1 };
      for (const def of SLICE_DEFS) {
        if (slices.includes(def.key)) {
          patch[`${def.key}CachedAt`] = null;
          _hydrating[def.key] = false;
          _retries[def.key] = 0;
        }
      }
      set(patch);
    },

    ...ensurers,

    resetAll: () => {
      resetHydrating();
      const patch = { systems: [], refetchSystems: null, _invalidationSeq: 0 };
      for (const def of SLICE_DEFS) {
        patch[def.dataField]             = def.isObject ? null : [];
        patch[`${def.key}Loading`]       = false;
        patch[`${def.key}CachedAt`]      = null;
        patch[`refetch${cap(def.key)}`]  = null;
      }
      return set(patch);
    },
  };
}, { name: 'coreStore' }));

export const getCoreStore = () => useCoreStore.getState();
export default useCoreStore;
